// Client for the Quikpl backend (REST).
//
// Auth: the backend sets a SameSite=Lax cookie on first request and reads it on every
// subsequent one. Because Studio and the API are same-origin (in dev via Vite proxy;
// in prod served by the same Fastify), the browser auto-attaches the cookie. No
// explicit header needed from the client.

export interface Project {
  id: string;
  userId: string;
  name: string;
  /** URL slug, set at first deploy. Null until deployed. Backs the pretty share URL. */
  slug: string | null;
  createdAt: number;
  updatedAt: number;
  /** Timestamp of the latest file write. The Studio uses
   *  `lastFileWriteAt > currentVersion.createdAt` to show a "deploy pending" state. */
  lastFileWriteAt: number;
  /** Tier 2 (hub) metadata. */
  description: string | null;
  tags: string[];
  isPublic: boolean;
  forkedFrom: string | null;
}

export interface ProjectFile {
  path: string;
  content: string;
}

export type ChatRole = "user" | "assistant" | "tool" | "system";
export interface ChatMessage {
  seq: number;
  role: ChatRole;
  content: unknown;
}

export interface ValidateResult {
  ok: boolean;
  consoleErrors: string[];
  consoleWarnings: string[];
  consoleLogs: string[];
  exceptions: string[];
  loadedMs: number;
}

export interface ScreenshotResult {
  imageBase64: string;
  width: number;
  height: number;
  loadedMs: number;
  consoleLogs: string[];
  consoleErrors: string[];
  exceptions: string[];
}

interface RequestOpts {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
}

async function req<T>(path: string, init: RequestOpts = {}): Promise<T> {
  const { body, method, headers: extraHeaders } = init;
  const headers: Record<string, string> = { ...extraHeaders };
  if (body !== undefined && body !== null) headers["Content-Type"] = "application/json";
  const res = await fetch(path, {
    method,
    headers,
    credentials: "same-origin", // browsers send cookies on same-origin by default; explicit for clarity
    body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { _text: text };
    }
  }
  if (!res.ok) {
    const err = json && typeof json === "object" && "error" in (json as object) ? (json as { error: string }).error : `HTTP ${res.status}`;
    throw new Error(err);
  }
  return json as T;
}

// Projects
export function listProjects(): Promise<{ projects: Project[] }> {
  return req("/api/projects");
}
export function createProject(name: string): Promise<{ project: Project }> {
  return req("/api/projects", { method: "POST", body: { name } });
}
export function getProject(id: string): Promise<{ project: Project; files: ProjectFile[]; chat: ChatMessage[] }> {
  return req(`/api/projects/${id}`);
}
export function renameProject(id: string, name: string): Promise<{ ok: true }> {
  return req(`/api/projects/${id}`, { method: "PATCH", body: { name } });
}
export function setProjectMetadata(
  id: string,
  patch: { description?: string; tags?: string[]; isPublic?: boolean }
): Promise<{ ok: true; project: Project }> {
  return req(`/api/projects/${id}/metadata`, { method: "PUT", body: patch });
}
export function deleteProject(id: string): Promise<{ ok: true }> {
  return req(`/api/projects/${id}`, { method: "DELETE" });
}

// Export / Import — round-trip a project as a zip (files + chat + metadata).
export async function exportProject(id: string, filenameHint?: string): Promise<void> {
  const res = await fetch(`/api/projects/${id}/export`, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`export failed: HTTP ${res.status}`);
  // Prefer the filename the server suggested in Content-Disposition.
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^";]+)"?/.exec(cd);
  const filename = m?.[1] ?? `${filenameHint ?? "project"}.quikpl.zip`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function importProject(file: File): Promise<{ project: Project }> {
  const buf = await file.arrayBuffer();
  // ArrayBuffer → base64. btoa works on a binary string, so chunk to avoid stack overflow.
  let bin = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
  }
  const zip = btoa(bin);
  return req("/api/projects/import", { method: "POST", body: { zip } });
}

// Files
export function listFiles(id: string): Promise<{ files: ProjectFile[] }> {
  return req(`/api/projects/${id}/files`);
}
export function readFile(id: string, path: string): Promise<{ path: string; content: string }> {
  return req(`/api/projects/${id}/file?path=${encodeURIComponent(path)}`);
}
export function writeFile(id: string, path: string, content: string): Promise<{ ok: true }> {
  return req(`/api/projects/${id}/file`, { method: "PUT", body: { path, content } });
}
export function deleteFile(id: string, path: string): Promise<{ ok: true }> {
  return req(`/api/projects/${id}/file?path=${encodeURIComponent(path)}`, { method: "DELETE" });
}

// Chat
export function listChat(id: string): Promise<{ chat: ChatMessage[] }> {
  return req(`/api/projects/${id}/chat`);
}
export function appendChat(id: string, role: ChatRole, content: unknown): Promise<{ message: ChatMessage }> {
  return req(`/api/projects/${id}/chat`, { method: "POST", body: { role, content } });
}
export function clearChat(id: string): Promise<{ ok: true }> {
  return req(`/api/projects/${id}/chat`, { method: "DELETE" });
}

// Deploy
export interface VersionMeta { id: string; createdAt: number; fileCount: number; }
export function deploy(id: string): Promise<{ version: VersionMeta; slug: string }> {
  return req(`/api/projects/${id}/deploy`, { method: "POST" });
}
export function getCurrentVersion(id: string): Promise<{ version: VersionMeta }> {
  return req(`/api/projects/${id}/current-version`);
}

// Who am I — effective owner id, raw cookie id, and the signed-in user (or null).
export interface PublicUser {
  id: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  avatarUrl: string | null;
}
export type BillingMode = "credits" | "byo";

export interface MeResult {
  userId: string;
  cookieId: string;
  user: PublicUser | null;
  needsUsername: boolean;
  suggestedUsername: string | null;
  /** Whether the signed-in user has a BYO Anthropic key stored server-side. Reported
   *  independently of billingMode — a credits user may still have a dormant key on file. */
  hasApiKey: boolean;
  /** The explicit billing path the user chose. Source of truth for which path bills a run. */
  billingMode: BillingMode;
  /** Prepaid credit balance (USD). Always reported regardless of mode. */
  creditBalanceUsd: number;
}
export function getMe(): Promise<MeResult> {
  return req("/api/me");
}

// ----- Credits (Tier 1.5C) -----
export interface CreditsResult {
  balanceMicro: number;
  balanceUsd: number;
  autoTopup: boolean;
  autoTopupAmountUsd: number;
  /** Worst-case cost of one build step per model — the auto-top-up TRIGGER (computed, locked
   *  to the chosen model; not user-set). */
  stepCostUsdByModel: Record<string, number>;
  /** Per-user balance cap (USD). The server rejects any top-up that would push past it. */
  maxBalanceUsd: number;
  spend: { todayUsd: number; weekUsd: number; totalUsd: number };
}
export function getAccountCredits(): Promise<CreditsResult> {
  return req("/api/account/credits");
}
export function setAutoTopup(opts: { autoTopup: boolean; autoTopupAmountUsd?: number }): Promise<CreditsResult> {
  return req("/api/account/credits/auto", { method: "PUT", body: opts });
}
/** Start a hosted Stripe Checkout for a preset top-up. Returns the URL to redirect to. */
export function startTopupCheckout(amountUsd: number): Promise<{ url: string }> {
  return req("/api/account/topup/checkout", { method: "POST", body: { amountUsd } });
}
export const TOPUP_PRESETS_USD = [10, 25, 50];

// ----- Account BYO API key (stored encrypted server-side; never returned to the client) -----
export function getAccountKeyStatus(): Promise<{ hasApiKey: boolean }> {
  return req("/api/account/api-key");
}
export function putAccountKey(apiKey: string): Promise<{ hasApiKey: boolean }> {
  return req("/api/account/api-key", { method: "PUT", body: { apiKey } });
}
export function deleteAccountKey(): Promise<{ hasApiKey: boolean }> {
  return req("/api/account/api-key", { method: "DELETE" });
}
export function setBillingMode(mode: BillingMode): Promise<{ billingMode: BillingMode }> {
  return req("/api/account/billing-mode", { method: "PUT", body: { mode } });
}

// ----- Agent runs (server-side loop + SSE stream) -----
// The builder loop runs on the backend so a build survives the client disconnecting. The
// client starts a run, streams its events over SSE, and can reconnect to an in-flight run.

export interface RunStreamEvent {
  seq: number;
  /** assistant_start | text | tool_use | tool_result | assistant_message | user_message | usage | error | done */
  type: string;
  data: any; // shape depends on type; the Workspace narrows per type
}
export interface RunStreamHandlers {
  onEvent: (ev: RunStreamEvent) => void;
  /** Stream ended (server closed it, or we aborted). Always fires exactly once. */
  onClose?: () => void;
  onError?: (err: Error) => void;
}

export function startRun(
  projectId: string,
  opts: { userText: string; model: string; reasoning: string }
): Promise<{ runId: string }> {
  return req(`/api/projects/${projectId}/runs`, { method: "POST", body: opts });
}
export function getActiveRun(projectId: string): Promise<{ run: { runId: string; lastSeq: number } | null }> {
  return req(`/api/projects/${projectId}/runs/active`);
}
export function stopRun(projectId: string, runId: string): Promise<{ stopped: boolean }> {
  return req(`/api/projects/${projectId}/runs/${runId}/stop`, { method: "POST" });
}

function parseSseChunk(chunk: string): RunStreamEvent | null {
  let event = "message";
  let id: string | null = null;
  const dataLines: string[] = [];
  let hasField = false;
  for (const line of chunk.split("\n")) {
    if (line.startsWith(":")) continue; // comment / heartbeat
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
      hasField = true;
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^ /, ""));
      hasField = true;
    } else if (line.startsWith("id:")) {
      id = line.slice(3).trim();
      hasField = true;
    }
  }
  if (!hasField) return null;
  let data: unknown = null;
  if (dataLines.length) {
    try {
      data = JSON.parse(dataLines.join("\n"));
    } catch {
      data = dataLines.join("\n");
    }
  }
  return { seq: id !== null ? Number(id) : -1, type: event, data };
}

/**
 * Open an SSE stream for a run. We use fetch + ReadableStream (not EventSource) so we can
 * pass `fromSeq` for reconnect and rely on same-origin cookie auth. Returns an abort
 * function; call it to close the stream early. `onClose` always fires once at the end.
 */
export function streamRun(
  projectId: string,
  runId: string,
  fromSeq: number,
  handlers: RunStreamHandlers
): () => void {
  const ac = new AbortController();
  void (async () => {
    let closed = false;
    const finish = () => {
      if (closed) return;
      closed = true;
      handlers.onClose?.();
    };
    try {
      const res = await fetch(`/api/projects/${projectId}/runs/${runId}/stream?fromSeq=${fromSeq}`, {
        headers: { Accept: "text/event-stream" },
        credentials: "same-origin",
        signal: ac.signal,
      });
      if (!res.ok || !res.body) {
        handlers.onError?.(new Error(`stream failed: HTTP ${res.status}`));
        finish();
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const ev = parseSseChunk(chunk);
          if (ev) handlers.onEvent(ev);
        }
      }
      finish();
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        finish();
        return;
      }
      handlers.onError?.(e as Error);
      finish();
    }
  })();
  return () => ac.abort();
}

// Auth — sign-in is a full-page redirect (not fetch) because it bounces through Google.
// We expose the URL builder; the caller does `window.location.href = googleSignInUrl()`.
export function googleSignInUrl(): string {
  return "/auth/google/start";
}
export function logout(): Promise<{ ok: true }> {
  return req("/auth/logout", { method: "POST" });
}

// ----- Hub (discovery) -----
export interface HubApp {
  projectId: string;
  name: string;
  description: string | null;
  tags: string[];
  slug: string;
  username: string;
  uses?: number;
}
export function hubLeaderboard(): Promise<{ apps: HubApp[]; basis: "usage" | "recent" }> {
  return req("/api/hub/leaderboard");
}
export function hubSearch(q: string): Promise<{ query: string; apps: HubApp[] }> {
  return req(`/api/hub/search?q=${encodeURIComponent(q)}`);
}
/** Of the given project ids, which are still openable (exist + deployed + named owner). */
export function hubResolve(ids: string[]): Promise<{ apps: HubApp[] }> {
  return req("/api/hub/resolve", { method: "POST", body: { ids } });
}
export function forkProject(id: string): Promise<{ project: Project }> {
  return req(`/api/projects/${id}/fork`, { method: "POST" });
}

// Stars
export function listMyStars(): Promise<{ apps: HubApp[] }> {
  return req("/api/stars");
}

/** Recently-played apps, read from top-origin localStorage written by the app wrapper. */
export interface RecentApp {
  id: string;
  name: string;
  path: string;
  ts: number;
}
const RECENT_KEY = "quikpl.recent";
export function recentlyPlayed(): RecentApp[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(raw) ? (raw as RecentApp[]).filter((r) => r && r.id && r.path) : [];
  } catch {
    return [];
  }
}
/** Overwrite the recently-played list (used to prune dead/deleted apps). */
export function setRecentlyPlayed(apps: RecentApp[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(apps));
  } catch {
    // ignore (private mode / quota)
  }
}

// Username flow
export function checkUsername(
  u: string
): Promise<{ valid: boolean; available: boolean; reason?: string; username?: string }> {
  return req(`/api/username/check?u=${encodeURIComponent(u)}`);
}
export function setUsername(username: string): Promise<{ user: PublicUser }> {
  return req("/api/username", { method: "POST", body: { username } });
}

// Tools
export interface IdentitySpec {
  label: string;
  identityId: string;
  path?: string;
  script?: string;
  waitMs?: number;
  scriptTimeoutMs?: number;
  /** Per-pane override: wipe just this identity's local KV before running. */
  fresh?: boolean;
}
export interface ToolOpts {
  width?: number;
  height?: number;
  waitMs?: number;
  path?: string;
  script?: string;
  /** Timeout for the `script` evaluation. Default 15000, max 60000. */
  scriptTimeoutMs?: number;
  /** Wipe per-identity local KV for all identities in this call before running.
   *  Test-only; bypasses `local.set('lastRoom')`-style auto-rejoin. */
  fresh?: boolean;
  identities?: IdentitySpec[];
}

export interface MultiValidateResult {
  ok: boolean;
  perIdentity: Array<{ label: string; identityId: string; outcome: ValidateResult }>;
}
export interface MultiScreenshotResult {
  width: number;
  height: number;
  perIdentity: Array<{
    label: string;
    identityId: string;
    imageBase64: string;
    loadedMs: number;
    consoleLogs: string[];
    consoleErrors: string[];
    exceptions: string[];
  }>;
}

export function validateProject(
  id: string,
  opts: ToolOpts = {}
): Promise<ValidateResult | MultiValidateResult> {
  return req(`/api/projects/${id}/tools/validate`, { method: "POST", body: opts });
}
export function screenshotProject(
  id: string,
  opts: ToolOpts = {}
): Promise<ScreenshotResult | MultiScreenshotResult> {
  return req(`/api/projects/${id}/tools/screenshot`, { method: "POST", body: opts });
}
